import { useState } from 'react';
import { Box, Button } from '@mui/material';
import { useChartColors } from './theme';
import { useTooltip } from './ChartTooltip';
import TableView from './TableView';
import { useChartWidth } from './useChartWidth';

export interface HStack100Row { label: string; segments: number[]; }
export interface HStack100Props {
    rows: HStack100Row[];
    seriesLabels: string[];
    height?: number;
    colorIndices?: number[];
}

// 100%-stacked horizontal bars — each row's segments are normalised to the full bar
// width so rows are comparable by share regardless of headcount. Segment i is painted
// with theme series color colorIndices[i] (default identity). Mirrors HBars' geometry.
export default function HStack100({ rows, seriesLabels, height, colorIndices }: HStack100Props) {
    const c = useChartColors();
    const { show, hide, node } = useTooltip();
    const [showTable, setShowTable] = useState(false);

    const rowH = 30, padL = 120, padR = 12;
    const [wrapRef, W] = useChartWidth();
    const H = height ?? (rows.length * rowH + 12);
    const plotW = W - padL - padR;
    const colors = colorIndices ?? rows[0]?.segments.map((_, i) => i) ?? [];
    const pct = (v: number, total: number) => (total > 0 ? Math.round((v / total) * 100) : 0);

    return (
        <Box ref={wrapRef}>
            <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ display: 'block', maxWidth: '100%' }} role="img" aria-label="100% stacked horizontal bar chart">
                {rows.map((row, i) => {
                    const y = 6 + i * rowH;
                    const total = row.segments.reduce((a, b) => a + b, 0);
                    const shown = row.label.length > 16 ? row.label.slice(0, 15) + '…' : row.label;
                    const tipText = `${row.label} — ${row.segments.map((v, s) => `${seriesLabels[s]}: ${v} (${pct(v, total)}%)`).join(', ')}`;
                    let acc = 0;
                    return (
                        <g key={i}
                           onMouseMove={e => show(e.clientX, e.clientY, tipText)}
                           onMouseLeave={hide}>
                            <text x={padL - 8} y={y + rowH / 2} textAnchor="end" dominantBaseline="middle" fontSize={11} fill={c.ink}>
                                {shown !== row.label && <title>{row.label}</title>}
                                {shown}
                            </text>
                            {row.segments.map((v, s) => {
                                const w = total > 0 ? plotW * (v / total) : 0;
                                const x = padL + acc;
                                acc += w;
                                return <rect key={s} x={x} y={y + 6} width={Math.max(0, w)} height={rowH - 14} fill={c.series[colors[s] ?? s]} />;
                            })}
                        </g>
                    );
                })}
            </svg>
            <Button size="small" onClick={() => setShowTable(s => !s)}>Table view</Button>
            {showTable && <TableView columns={['Label', ...seriesLabels]} rows={rows.map(r => [r.label, ...r.segments.map(v => String(v))])} />}
            {node}
        </Box>
    );
}
